// src/components/personal/sections/ClientsSection/hooks/useClientSorting.ts
import { useState, useCallback, useMemo } from 'react';
import { Client } from '../types/types';
import { sortClients } from '../utils/clientFilters';

type SortDirection = 'asc' | 'desc';

interface UseClientSortingProps {
  clients: Client[];
  initialSortColumn?: string;
  initialSortDirection?: SortDirection;
  onSortChange?: (column: string, direction: SortDirection) => void;
}

interface UseClientSortingReturn {
  // Состояние сортировки
  sortColumn: string;
  sortDirection: SortDirection;
  sortedClients: Client[];
  
  // Методы управления сортировкой
  setSorting: (column: string, direction: SortDirection) => void;
  toggleSortDirection: () => void;
  sortBy: (column: string) => void;
}

export const useClientSorting = ({
  clients,
  initialSortColumn = 'name',
  initialSortDirection = 'asc',
  onSortChange
}: UseClientSortingProps): UseClientSortingReturn => {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние сортировки
  const [sortColumn, setSortColumn] = useState<string>(initialSortColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSortDirection);
  
  // Метод установки параметров сортировки
  const setSorting = useCallback((column: string, direction: SortDirection) => {
    setSortColumn(column);
    setSortDirection(direction);
    
    if (onSortChange) {
      onSortChange(column, direction);
    }
  }, [onSortChange]);
  
  // Метод переключения направления сортировки
  const toggleSortDirection = useCallback(() => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    
    if (onSortChange) {
      onSortChange(sortColumn, newDirection);
    }
  }, [sortColumn, sortDirection, onSortChange]);
  
  // Метод сортировки по колонке
  const sortBy = useCallback((column: string) => {
    // Если колонка та же, меняем направление
    if (column === sortColumn) {
      toggleSortDirection();
    } else {
      // Иначе устанавливаем новую колонку с направлением по умолчанию (asc)
      setSorting(column, 'asc');
    }
  }, [sortColumn, toggleSortDirection, setSorting]);
  
  // Отсортированный список клиентов
  const sortedClients = useMemo(() => {
    if (clients.length === 0) {
      return [];
    }
    
    if (DEBUG) console.log(`Сортировка ${clients.length} клиентов по ${sortColumn} (${sortDirection})`);
    
    return sortClients(clients, sortColumn, sortDirection);
  }, [clients, sortColumn, sortDirection, DEBUG]);
  
  return {
    sortColumn,
    sortDirection,
    sortedClients,
    setSorting,
    toggleSortDirection,
    sortBy
  };
};